import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { listBookings } from '../api/endpoints/bookings';
import { BookingRow } from '../components/BookingRow';
import { BookingFeesMonth } from '../components/BookingFees';
import { BusinessRatingsCard } from '../components/BusinessRatingsCard';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingState } from '../components/LoadingState';
import { Notice } from '../components/Notice';
import { PageHeader } from '../components/PageHeader';
import { ReservationLinkCard } from '../components/ReservationLinkCard';
import { ReservationsSummary } from '../components/ReservationsSummary';
import { formatAgendaDayLabel, groupAgenda } from '../lib/bookings';
import { agendaWindow, bookingsQueryKey } from '../lib/bookingWindows';
import { isSameDay } from '../lib/format';
import { useBusiness } from '../lib/useBusiness';

/** A new reservation shows up within half a minute; the Agenda never needed 15 s. */
const BOOKINGS_REFETCH_INTERVAL_MS = 30_000;


type InboxNotice = 'solo-dueno' | 'sin-permiso';

type InboxPageProps = {
  /** The '/' route passes the solo-dueno/sin-permiso bounce notice; /reservas never does. */
  notice?: InboxNotice;
};

function noticeMessage(notice: InboxNotice): ReactNode {
  if (notice === 'solo-dueno') return 'Solo la persona dueña puede entrar ahí.';
  return 'Tu perfil no tiene acceso a esa sección. Pídeselo a la persona dueña.';
}

export function InboxPage({ notice }: InboxPageProps = {}) {
  const business = useBusiness();
  const range = agendaWindow(new Date());
  const bookingsQuery = useQuery({
    queryKey: bookingsQueryKey(range),
    queryFn: () => listBookings(range),
    refetchInterval: BOOKINGS_REFETCH_INTERVAL_MS,
  });

  const now = useMemo(() => new Date(), [bookingsQuery.dataUpdatedAt]);
  const agenda = useMemo(() => {
    if (!bookingsQuery.data) return undefined;
    return groupAgenda(bookingsQuery.data.data, now);
  }, [bookingsQuery.data, now]);

  return (
    <main>
      <PageHeader title="Reservas" description="Por cerrar, hoy y lo que viene." />
      {notice ? <Notice>{noticeMessage(notice)}</Notice> : null}
      {bookingsQuery.data ? <ReservationsSummary bookings={bookingsQuery.data.data} now={now} business={business} /> : null}
      <ReservationLinkCard />
      {bookingsQuery.isLoading ? <LoadingState label="Cargando reservas…" /> : null}
      {bookingsQuery.isError ? <>
        <ErrorBanner message={errorMessage(bookingsQuery.error)} />
        <button type="button" className="btn small secondary" disabled={bookingsQuery.isFetching} onClick={() => void bookingsQuery.refetch()}>Reintentar reservas</button>
      </> : null}
      {agenda ? (
        <>
          {agenda.porCerrar.length > 0 ? (
            <section aria-label="Por cerrar" className="section">
              <h2 className="section-label">Por cerrar</h2>
              <ul className="rows agenda-list stagger">
                {agenda.porCerrar.map((booking) => (
                  <BookingRow
                    key={booking.id}
                    booking={booking}
                    showComplete
                    {...(isSameDay(new Date(booking.startsAt), now)
                      ? {}
                      : { dayNote: dayNoteFor(new Date(booking.startsAt), now) })}
                  />
                ))}
              </ul>
            </section>
          ) : null}
          <section aria-label="Hoy" className="section">
            <h2 className="section-label">Hoy</h2>
            {agenda.hoy.length === 0 && agenda.porCerrar.length === 0 ? (
              <EmptyState
                title="Hoy está tranquilo."
                description="Las reservas de hoy viven aquí; las canceladas quedan en Historial."
              />
            ) : agenda.hoy.length === 0 ? (
              <p className="row-sub">Nada más por hoy.</p>
            ) : (
              <ul className="rows agenda-list stagger">
                {agenda.hoy.map((booking) => (
                  <BookingRow key={booking.id} booking={booking} />
                ))}
              </ul>
            )}
          </section>
          {agenda.proximas.map((day) => (
            <section
              key={day.key}
              aria-label={formatAgendaDayLabel(new Date(day.bookings[0]!.startsAt), now)}
              className="section"
            >
              <h2 className="section-label">
                {formatAgendaDayLabel(new Date(day.bookings[0]!.startsAt), now)}
                <span className="section-count" aria-hidden="true">{day.bookings.length}</span>
              </h2>
              <ul className="rows agenda-list stagger">
                {day.bookings.map((booking) => (
                  <BookingRow key={booking.id} booking={booking} />
                ))}
              </ul>
            </section>
          ))}
          <BusinessRatingsCard />
          <BookingFeesMonth />
        </>
      ) : null}
    </main>
  );
}

/** 'Ayer' when it was yesterday; the short date for anything older. */
function dayNoteFor(date: Date, now: Date): string {
  const yesterday = new Date(now.getTime());
  yesterday.setDate(yesterday.getDate() - 1);
  if (isSameDay(date, yesterday)) return 'Ayer';
  return date.toLocaleDateString('es-PA', { day: 'numeric', month: 'short' });
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos cargar las reservas.';
}
